import { isCookieConsentValid } from "./cookie-consent-validity.mjs";
import { VOUCHER_PATH_SEGMENTS } from "./slug-paths.js";

/** Backend route that receives one newsletter product-click beacon per landing. */
export const NEWSLETTER_CLICK_PATH = "/api/v2/newsletter/product-click";
/** Payload contract version the backend DTO validates against; bump together with the backend. */
export const NEWSLETTER_CLICK_CONTRACT_VERSION = "newsletter-product-click/v1";
// Browser-local, pseudonymous 256-bit value. Never copied to a cookie.
export const NEWSLETTER_ANONYMOUS_SESSION_STORAGE_KEY =
  "favotrip.newsletterAnonymousSessionToken.v1";
/** Storage key under which the parser-time head script parks a landing token for the beacon. */
export const NEWSLETTER_PENDING_CLICK_STORAGE_KEY =
  "favotrip.newsletterPendingClick.v1";
/** Lifetime of the consented localStorage identifier: thirty days, then a fresh token is minted. */
export const NEWSLETTER_TOKEN_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const SESSION_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;
const PRODUCT_LINK_TOKEN_PATTERN =
  /^[a-z0-9][a-z0-9_-]{0,31}\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]{43}$/;

/** True only when the build-time flag is explicitly on; the beacon is off by default. */
export function isNewsletterClickBeaconEnabled(value) {
  return value === "1";
}

function bytesToBase64Url(bytes) {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary)
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replace(/=+$/, "");
}

/** Client-side shape check mirroring the backend token regex, so junk never costs a beacon or throttle slot. */
export function isValidNewsletterProductLinkToken(value) {
  return (
    typeof value === "string" &&
    value.length <= 4096 &&
    PRODUCT_LINK_TOKEN_PATTERN.test(value)
  );
}

/** Reads the stored cookie preference and applies the shared six-month validity rule before granting persistence. */
export function readNewsletterIdentifierConsent(storage, now = Date.now()) {
  if (!storage) return false;
  try {
    const raw = storage.getItem("cookie_consent_prefs");
    if (raw) {
      const prefs = JSON.parse(raw);
      return (
        isCookieConsentValid(prefs, now) &&
        (prefs.analytics === true || prefs.marketing === true)
      );
    }
    return false;
  } catch {
    return false;
  }
}

/** Picks localStorage only with valid analytics or marketing consent, otherwise the session-bound store. */
export function getNewsletterIdentifierStorage(
  persistentStorage,
  sessionStorage,
  now = Date.now(),
) {
  const persistent = readNewsletterIdentifierConsent(persistentStorage, now);
  return {
    storage: persistent ? persistentStorage : sessionStorage,
    persistent,
  };
}

/** Removes the persistent identifier, used when a visitor withdraws consent. */
export function clearNewsletterPersistentIdentifier(storage) {
  try {
    storage?.removeItem(NEWSLETTER_ANONYMOUS_SESSION_STORAGE_KEY);
  } catch {
    // Consent withdrawal cleanup is best-effort for blocked storage.
  }
}

/** Returns the current identifier or mints a 43-character base64url token, honouring expiry and consent. */
export function getOrCreateAnonymousSessionToken(
  storage,
  cryptoApi,
  { persistent = false, now = Date.now() } = {},
) {
  if (!storage || typeof cryptoApi?.getRandomValues !== "function") return null;

  try {
    const raw = storage.getItem(NEWSLETTER_ANONYMOUS_SESSION_STORAGE_KEY);
    if (persistent && raw) {
      try {
        const existing = JSON.parse(raw);
        if (
          SESSION_TOKEN_PATTERN.test(existing?.token || "") &&
          Number.isFinite(existing?.expiresAt) &&
          existing.expiresAt > now
        ) {
          return existing.token;
        }
      } catch {
        // Replace legacy/malformed values below.
      }
    } else if (!persistent && SESSION_TOKEN_PATTERN.test(raw || "")) {
      return raw;
    }

    const bytes = new Uint8Array(32);
    cryptoApi.getRandomValues(bytes);
    const token = bytesToBase64Url(bytes);
    storage.setItem(
      NEWSLETTER_ANONYMOUS_SESSION_STORAGE_KEY,
      persistent
        ? JSON.stringify({ token, expiresAt: now + NEWSLETTER_TOKEN_TTL_MS })
        : token,
    );
    return token;
  } catch {
    return null;
  }
}

function createUuidV4(cryptoApi) {
  const bytes = new Uint8Array(16);
  cryptoApi.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0"));
  return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10).join("")}`;
}

/** Builds the beacon body exactly as the backend contract expects it. */
export function createNewsletterClickPayload({
  token,
  anonymousSessionToken,
  cryptoApi,
  now = new Date(),
}) {
  if (typeof cryptoApi?.getRandomValues !== "function") return null;
  return {
    token,
    eventId: createUuidV4(cryptoApi),
    clickedAt: now.toISOString(),
    anonymousSessionToken,
    contractVersion: NEWSLETTER_CLICK_CONTRACT_VERSION,
  };
}

/** Returns the same URL without the one-time nlt parameter, keeping every other query part and the hash. */
export function stripNewsletterToken(urlHref) {
  const url = new URL(urlHref);
  url.searchParams.delete("nlt");
  return `${url.pathname}${url.search}${url.hash}`;
}

/** Landing gate: exactly the three URL shapes the mailer emits, with an optional locale prefix. */
export function isNewsletterClickLandingPath(pathname) {
  const voucherSegment = pathname.split("/").filter(Boolean).at(-2);
  return (
    /^\/(?:[^/]+\/)?packages\/[^/]+\/[^/]+\/?$/.test(pathname) ||
    /^\/(?:[^/]+\/)?rondreizen\/[^/]+\/?$/.test(pathname) ||
    (/^\/(?:[^/]+\/)?[^/]+\/[^/]+\/?$/.test(pathname) &&
      VOUCHER_PATH_SEGMENTS.includes(voucherSegment))
  );
}
